"use client";

import React from "react";

import config from "@/config";
import useCache from "@/hooks/useCache";
import useStateHandler from "@/hooks/useStateHandler";
import NavbarHomeNavigation from "@/layouts/navigation/components/NavbarHomeNavigation";
import PostListItem from "@/pages/profile/board/components/PostListItem";
import fetchWrapper from "@/services/fetchWrapper.service";
import CommunityArticleCard from "@/shared-components/cards/components/CommunityArticleCard";
import CommunityArtlistCard from "@/shared-components/cards/components/CommunityArtlistCard";
import CommunityNotRatedCard from "@/shared-components/cards/components/CommunityNotRatedCard";
import CommunityRatingCard from "@/shared-components/cards/components/CommunityRatingCard";
import CommunityThoughtCard from "@/shared-components/cards/components/CommunityThoughtCard";
import NewArtworkCard from "@/shared-components/cards/components/NewArtworkCard";
import { DropdownSort } from '@/shared-components/dropdown';
import HomeNavigation from "@/shared-components/home/components/HomeNavigation";
import InfiniteScroll from "@/shared-components/lists/components/InfiniteScroll";
import Loader from "@/shared-components/loader/Loader";
import StaticSeoHead from "@/shared-components/seo/StaticSeoHead";
import AppNavigationPage from "@/shared-components/wrappers/components/AppNavigationPage";
import { hasRenderableRating, hasRenderableThought } from "@/utils/activityContent";
import { getSsrDiscoverBootstrap } from "@/utils/ssrDiscoverBootstrap";

import "./index.css";

function readInitialCommunitySeoPayload() {
  const payload = getSsrDiscoverBootstrap('community');
  return payload?.seoData && typeof payload.seoData === 'object' ? payload.seoData : null;
}

export default function HomeCommunity() {
  const { state } = useStateHandler();
  const [filterOption, setFilterOption] = React.useState("All");
  const [communitySeo, setCommunitySeo] = React.useState(() => readInitialCommunitySeoPayload());

  // query estable
  const queryParams = React.useMemo(
    () => (filterOption === "Following" ? { only_following: "true" } : {}),
    [filterOption]
  );

  const cacheKey = React.useMemo(
    () => `home_community_${filterOption === "Following" ? "following" : "all"}`,
    [filterOption]
  );

  const { loading, fetchData, loadMoreData, refetch } = useCache(
    cacheKey,
    `${config.apis.api.url}/block9/community`,
    {
      type: "@cache/dynamic",
      injectToken: filterOption === "Following",
      optionalToken: filterOption !== "Following",
      includeSearchQueries: queryParams,
    }
  );

  // ---- FIX del loop: no dependas de la identidad de `refetch`
  const refetchRef = React.useRef(refetch);
  React.useEffect(() => {
    refetchRef.current = refetch;
  }, [refetch]);

  React.useEffect(() => {
    refetchRef.current && refetchRef.current();
  }, [filterOption]);

  React.useEffect(() => {
    let ignore = false;

    async function loadCommunitySeo() {
      try {
        const { response, data } = await fetchWrapper.get(`${config.apis.api.url}/seo/discover/community`);
        if (ignore || !response?.ok || !data || typeof data !== 'object') {
          return;
        }

        setCommunitySeo(data);
      } catch (error) {
        if (!ignore) {
          console.error('Failed to load community SEO payload', error);
        }
      }
    }

    loadCommunitySeo();

    return () => {
      ignore = true;
    };
  }, []);

  const isLoading = loading && (!fetchData || !fetchData.data);
  const items = fetchData?.data ?? [];
  const total = fetchData?.totalDocs ?? 0;

  const renderItem = (item) => {
    const data = item?.data ?? {};
    switch (item?.type) {
    case "post":
      return <PostListItem data={data} />;

    case "article":
      return <CommunityArticleCard article={data} />;

    case "thought":
      if (!hasRenderableThought(data)) {
        return null;
      }
      return <CommunityThoughtCard data={data} />;

    case "rating": {
      if (!hasRenderableRating(data)) {
        return null;
      }

      const currentUserId = state?.user_session?._id;
      const ratingUser = data?.rating_creator;
      const ratingCreatorId =
          typeof ratingUser === "string" ? ratingUser : ratingUser?._id;
      const isCreator = ratingCreatorId === currentUserId;
      const myRating = data.my_rating || data.myRating || (isCreator ? data : null);
      const hasRated = Boolean(myRating);

      const normalized = {
        ...data,
        rating_artwork: {
          ...data.rating_artwork,
          slug: data?.rating_artwork?.slug || data?.rating_artwork?._id,
        },
        ...(hasRated && myRating?.rating_values
          ? { my_rating: { rating_values: myRating.rating_values } }
          : {}),
      };

      return hasRated
        ? <CommunityRatingCard   data={normalized} ratingUser={ratingUser} />
        : <CommunityNotRatedCard data={normalized} ratingUser={ratingUser} />;
    }

    case "artlist": {
      const artlist = data?.artlist || data;
      const count = artlist?.artlist_artworks?.length || 0;
      if (![10, 20, 30].includes(count)) return null;
      return <CommunityArtlistCard data={data} />;
    }

    case "artwork": {
      const uploader = data?.artwork_about?.artwork_gallery || item?.artwork_user;
      return <NewArtworkCard data={data} user={uploader} />;
    }

    default:
      console.warn("[COMMUNITY] tipo no soportado:", item);
      return null;
    }
  };

  const renderableItems = items
    .map((item, index) => {
      const element = renderItem(item);
      if (!element) return null;
      const key = `${item?.type}-${item?.data?._id ?? index}`;
      return { key, element };
    })
    .filter(Boolean);

  return (
    <AppNavigationPage>
      <StaticSeoHead
        title={communitySeo?.title || "Art Community | Suarte"}
        description={communitySeo?.metaDescription || "Join Suarte's art community and explore the latest conversations around artworks, articles, posts, ratings, thoughts, and curated artlists from contemporary artists, galleries, and art lovers."}
        canonicalUrl={communitySeo?.canonicalUrl}
        canonicalPath={communitySeo?.canonicalPath || "/community"}
        robots={communitySeo?.robots}
        ogType={communitySeo?.ogType}
        image={communitySeo?.ogImage}
        pageType="CollectionPage"
        structuredData={communitySeo?.structuredData}
        breadcrumbItems={[
          { name: 'Home', url: 'https://suarte.art/' },
          { name: 'Community', url: 'https://suarte.art/community' },
        ]}
      />
      <div className="home-community__page">
        <HomeNavigation active="community" />
        <div className="home-community__navbar">
          <NavbarHomeNavigation active="community" />
        </div>
        <div className="home-community__content">
          {isLoading ? (
            <Loader thin />
          ) : (
            <>
              <div className="home-community__controls">
                <DropdownSort
                  defaultOption={filterOption}
                  options={["All", "Following"]}
                  onChangeOption={setFilterOption}
                />
              </div>

              {renderableItems.length > 0 && (
                <InfiniteScroll
                  key={cacheKey}                                  // resetea al cambiar filtro
                  onLoadMore={loadMoreData}
                  shouldLoadMore={!loading && items.length < total} // no cargar si ya está cargando
                  listLength={renderableItems.length}
                  className="home-community__infinite-scroll"
                >
                  {({ infiniteScrollAssigner }) => (
                    <div className="home-community__items">
                      {renderableItems.map((item, index) => (
                        <div
                          className="home-community__item"
                          key={item.key}
                          ref={infiniteScrollAssigner(index)}
                        >
                          {item.element}
                        </div>
                      ))}
                    </div>
                  )}
                </InfiniteScroll>
              )}
            </>
          )}
        </div>
      </div>
    </AppNavigationPage>
  );
}
